import type {
  AccountKind,
  DerivedAccount,
  SourceEntity,
  TransactionListDto,
} from "../api/types";

/**
 * The account label a transaction is shown under.
 *
 * There is no stored `account` field: the label is fully determined by facts
 * the row already has — what *kind* of account its source is, how its
 * expense type classifies it, and, for a transfer, which own-account it
 * faces. List rows carry the server's answer as `derivedAccount`; the
 * details page derives it client-side with the same rule (`derivedAccount`).
 */

/**
 * A list row without its derived projection — the shape the details DTO
 * shares with it. Code that only reads stored fields takes this, so it can
 * be handed either.
 */
export type StoredTransaction = Omit<TransactionListDto, "derivedAccount">;

/** The "not classified" label, shared with the expense-type registry. */
export const UNCLASSIFIED = "Ukjent";

const LABELS: Record<DerivedAccount, string> = {
  savings: "Sparing",
  creditCardFixed: "Kredittkort fast",
  creditCardVariable: "Kredittkort variabel",
};

/** The label for a derived account; `null` (nothing derives) is `Ukjent`. */
export const derivedAccountLabel = (
  value: DerivedAccount | null | undefined,
): string => (value ? LABELS[value] : UNCLASSIFIED);

/** True for a label that classifies nothing: empty, or the `Ukjent` marker. */
export const isUnclassified = (name: string | null | undefined): boolean => {
  const trimmed = name?.trim() ?? "";
  return trimmed === "" || trimmed.toLowerCase() === UNCLASSIFIED.toLowerCase();
};

/**
 * Derive the account, or `null` when the rule has nothing to say. Mirrors
 * the server's rule for the list rows; used where a row arrives without it.
 *
 * - A savings account is `savings`, whatever the row is.
 * - A credit card is split by the expense type: fixed costs are
 *   `creditCardFixed`, everything else classified is `creditCardVariable`.
 *   Fixed-vs-variable is read off the expense type's *name*: the expense
 *   types are `Fast Måned` and `Fast År` (fixed) against `Variabel Måned`,
 *   `Engangs`, `Utlegg Jobb` and `Utlegg Mamma` (variable), so a name that
 *   starts with "Fast" is fixed. `Ukjent` means not classified, so nothing
 *   is derived from it.
 * - A checking account derives `savings` only when the row is a transfer to
 *   or from a savings account (the counterparty, resolved from the row's
 *   counter-account number); an ordinary purchase derives nothing.
 * - Loans, investments and untyped sources derive nothing.
 */
export const derivedAccount = (
  sourceKind: AccountKind | null | undefined,
  expenseTypeName: string | null | undefined,
  counterparty: SourceEntity | undefined,
): DerivedAccount | null => {
  switch (sourceKind) {
    case "savings":
      return "savings";
    case "creditCard": {
      if (isUnclassified(expenseTypeName)) return null;
      const name = expenseTypeName?.trim() ?? "";
      return /^fast/i.test(name) ? "creditCardFixed" : "creditCardVariable";
    }
    case "checking":
      return counterparty?.kind === "savings" ? "savings" : null;
    default:
      return null;
  }
};
